import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ChevronDown } from "lucide-react";
import { useSaunaBasePrice } from "../hooks/useSaunaBasePrice";

const models = [
  { name: "CORTINA", saunaKey: "cortina", fallback: 299000, desc: "Store glassflater som åpner badstuen helt ut mot utsikten. Här handler det om å nyte hvert øyeblikk.", image: "https://media.base44.com/images/public/69a726345560d5ef49a37111/5f9a650a8_CortinaWhite.jpg", href: "/sauna-cortina", size: "4–6 personer", dims: "220 × 220 cm", altText: "LYKKE CORTINA – premium badstue med store panoramavinduer" },
  { name: "DOLOMITI", saunaKey: "dolomiti", fallback: 349000, desc: "Glasering som gjør naturen til en del av badstueopplevelsen. Minimalistisk, men med stor innvirkning.", image: "https://media.base44.com/images/public/69a726345560d5ef49a37111/c109e0135_Dolomiti_Thermo2.jpg", href: "/sauna-dolomiti", size: "6–8 personer", dims: "240 × 220 cm", altText: "LYKKE DOLOMITI – moderne badstue med minimalistisk design" },
  { name: "VIKKA", saunaKey: "vikka", fallback: 329000, desc: "Lys og åpen. En badstue som lar deg være nær naturen uten å skulle gjøre noe ekstra.", image: "https://media.base44.com/images/public/69a726345560d5ef49a37111/d62bfa839_Vikka012.jpg", href: "/sauna-vikka", size: "4–6 personer", dims: "340 × 240 cm", altText: "LYKKE VIKKA – åpen og lysåpen badstue modell" },
  { name: "CHAMONIX", saunaKey: "chamonix", fallback: 279000, desc: "Enkelt, rent og funksjonelt. Det finnes skjønnhet i det som ikke er unødvendig komplisert.", image: "https://media.base44.com/images/public/69a726345560d5ef49a37111/a39d226ee_Chamo10.jpg", href: "/sauna-chamonix", size: "4–6 personer", dims: "400 × 240 cm", altText: "LYKKE CHAMONIX – enkel og funksjonell badstue" },
  { name: "TRYSIL", saunaKey: "trysil", fallback: 359000, desc: "Her sitter du warm inni, og utsikten gjør resten. En kombinasjon som bare fungerer.", image: "https://media.base44.com/images/public/69a726345560d5ef49a37111/73e5fcce7_LYKKETRYSIL_long_2.jpg", href: "/sauna-trysil", size: "6–8 personer", dims: "440 × 240 cm", altText: "LYKKE TRYSIL – badstue med utsikt og naturlig lys" },
  { name: "GEILO", saunaKey: "geilo", fallback: 399000, desc: "Når alt skal stemme helt, og du ikke gjør kompromisser på arkitektur eller utsikt.", image: "https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/643d3d899_Geilo.jpg", href: "/sauna-geilo", size: "6–8 personer", dims: "350 × 230 cm", altText: "LYKKE GEILO – premium badstue modell med maksimal arkitektur" },
];

function ModelCard({ model }) {
  const { basePrice, loading } = useSaunaBasePrice(model.saunaKey, model.fallback);
  return (
    <a href={model.href} className="group cursor-pointer block bg-secondary/30 border border-border/40 p-8 hover:border-border transition-all duration-300 hover:bg-secondary/50">
      <h3 className="font-heading text-3xl md:text-4xl font-light mb-3 transition-colors duration-300 group-hover:text-foreground/70">LYKKE {model.name}</h3>
      <p className="text-foreground/60 text-2xl leading-relaxed mb-6 w-full transition-colors duration-300 group-hover:text-foreground/40">{model.desc}</p>
      <div className="aspect-[4/3] overflow-hidden mb-6 bg-secondary/50 relative">
        <img
          src={model.image}
          alt={model.altText}
          className="w-full h-full object-cover group-hover:scale-105 transition-all duration-[1.5s]"
          width="800"
          height="600"
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="space-y-1.5 text-2xl font-body border-t border-border/40 pt-4 transition-colors duration-300">
        <p className="text-foreground/60 transition-colors duration-300 group-hover:text-foreground/50">Størrelse: <strong className="text-foreground transition-colors duration-300 group-hover:text-foreground/70">{model.size}</strong></p>
        <p className="text-foreground/60 transition-colors duration-300 group-hover:text-foreground/50">Dimensjoner: <strong className="text-foreground transition-colors duration-300 group-hover:text-foreground/70">{model.dims}</strong></p>
        <p className="text-foreground/60 transition-colors duration-300 group-hover:text-foreground/50">Fra: <strong className="text-foreground transition-colors duration-300 group-hover:text-foreground/70">{loading ? "..." : `${basePrice.toLocaleString("nb-NO")} NOK`}</strong></p>
      </div>
    </a>
  );
}

export default function SaunaCollectionPanorama() {
  return (
    <>
      <Navbar />
      
      <section className="relative h-screen w-full overflow-hidden">
        <img
          src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/f4f8b636c_Panorama_Detal.jpg"
          alt="LYKKE PANORAMA Kolekcja – moderne badstuer med store glassfelt"
          className="absolute inset-0 w-full h-full object-cover"
          width="1920"
          height="1080"
          loading="eager"
          fetchpriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/10" />

        <div className="relative z-10 h-full flex flex-col justify-end pb-32 md:pb-40 px-6 md:px-12 max-w-[1440px] mx-auto">
          <p className="text-sm tracking-widest-2xl font-body uppercase text-white/50 mb-6">
            Koleksjon
          </p>
          <h1 className="font-heading text-white text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-light leading-[0.95] mb-6">
            LYKKE PANORAMA
            <br />
            <span className="italic opacity-60">Moderne panoramisk</span>
          </h1>
          <p className="text-white/70 text-sm md:text-base tracking-widest-2xl font-body uppercase">
            Moderne flate tak - maksimal glasering
          </p>
        </div>

        <button
          onClick={() => {
            document.querySelector("#models")?.scrollIntoView({ behavior: "smooth" });
          }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/50 hover:text-white transition-colors animate-bounce"
        >
          <ChevronDown size={28} strokeWidth={1} />
        </button>
      </section>

      <section className="py-16 md:py-24 bg-secondary/50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl md:text-5xl font-light leading-[1.2] mb-6">
              Moderne flate tak
            </h2>
            <p className="font-body text-2xl leading-relaxed text-foreground/70">
              LYKKE PANORAMA er basert på en enkel og tydelig form. Flatt tak og rene linjer gir et rolig uttrykk som passer både moderne boliger og mer klassisk arkitektur.
              <br />
              <br />
              Store vindusflater gir kontakt med omgivelsene og slipper inn godt med lys, uten å dominere konstruksjonen.
              <br />
              <br />
              En fleksibel løsning som fungerer i ulike sammenhenger.
            </p>
          </div>
        </div>
      </section>

      <section id="models" className="py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-6">
            {models.map((model) => (
              <ModelCard key={model.name} model={model} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-40 bg-secondary/50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">
                God stemning
              </p>
              <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-light leading-[1.1] mb-8">
                Ekte
                <br />
                <span className="italic">materialer</span>
              </h2>
              <p className="font-body leading-relaxed text-foreground/70 mb-6" style={{fontSize: "23px"}}>
                Alle LYKKE badstuer bygges i tre og naturlige materialer valgt for sin holdbarhet, varme og karakter.
                <br /><br />
                Når badstuen varmes opp, fylles rommet med en behagelig duft som skaper en rolig og avslappende stemning. Det er nettopp den følelsen vi elsker med en ekte badstue.
              </p>
            </div>
            <div className="aspect-[3/4] overflow-hidden rounded-lg bg-transparent flex items-center justify-center">
              <img src="https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/f4a2a3e59_vellykket-badstue_stacked-full_clay.png" alt="LYKKE PANORAMA – illustrasjon av alle badstuemodeller" className="w-full h-full object-contain" width="600" height="800" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center">
          <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">
            Kom i gang
          </p>
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-light mb-8">
            Finn badstuen
            <br />
            <span className="italic">som passer deg</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => window.location.href = "/process"}
              className="text-base tracking-widest-xl font-body uppercase px-8 py-3.5 border border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300">
              BLI KJENT MED PROSESSEN
            </button>
            <button
              onClick={() => window.location.href = "/sauna-ly"}
              className="text-base tracking-widest-xl font-body uppercase px-8 py-3.5 border border-foreground/30 text-foreground/70 hover:border-[#7a6f63] hover:bg-[#7a6f63] hover:text-white transition-all duration-300">
              LYKKE LY
            </button>
            <button
              onClick={() => window.location.href = "/sauna-fjel"}
              className="text-base tracking-widest-xl font-body uppercase px-8 py-3.5 border border-foreground/30 text-foreground/70 hover:border-[#7a6f63] hover:bg-[#7a6f63] hover:text-white transition-all duration-300">
              LYKKE FJELL
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}